import React, { useEffect, useState } from 'react';
import {
  PRODUCTION_CAPITAL_CAMPAIGN,
  listActiveProductionCapitalPackages,
  computeProductionCapitalBreakdown,
  isProductionCapitalCampaignActive,
  type ProductionCapitalPackageId,
} from '../constants';

/**
 * Production Capital Share Campaign — P1–P5 package cards only.
 * Layout: compact CSS grid (5-across on wide, 3+2 mid, 2/1 narrow) so cards
 * never clip the hero / dashboard edges.
 * Localhost: VITE_PRODUCTION_CAPITAL_FORCE_ACTIVE=true to preview before go-live.
 */

const CampaignCountdown: React.FC = () => {
  const target = React.useMemo(() => Date.parse(PRODUCTION_CAPITAL_CAMPAIGN.endDate), []);
  const compute = React.useCallback(() => {
    const distance = Math.max(0, target - Date.now());
    return {
      days: Math.floor(distance / (1000 * 60 * 60 * 24)),
      hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((distance % (1000 * 60)) / 1000),
    };
  }, [target]);
  const [t, setT] = useState(compute);
  useEffect(() => {
    setT(compute());
    const id = setInterval(() => setT(compute()), 1000);
    return () => clearInterval(id);
  }, [compute]);
  const cell = (value: number, label: string) => (
    <div className="pcap-countdown-cell">
      <div className="pcap-countdown-value">{value}</div>
      <div className="pcap-countdown-label">{label}</div>
    </div>
  );
  return (
    <div className="pcap-countdown" aria-label="Campaign countdown">
      {cell(t.days, 'D')}
      {cell(t.hours, 'H')}
      {cell(t.minutes, 'M')}
      {cell(t.seconds, 'S')}
    </div>
  );
};

interface Props {
  className?: string;
  onLaunchPackage?: (packageId: ProductionCapitalPackageId) => void;
  /** @deprecated BOGO-era prop; ignored — use onLaunchPackage */
  onLaunchStandard?: () => void;
}

export const ProductionCapitalPackageButtons: React.FC<Props> = ({ className = '', onLaunchPackage }) => {
  // Counter A sold unknown on static cards — date/force gate only for offer visibility.
  if (!isProductionCapitalCampaignActive({ paidSharesSold: 0 })) return null;

  const packages = listActiveProductionCapitalPackages();

  const launch = (packageId: ProductionCapitalPackageId) => {
    try {
      window.sessionStorage.removeItem('aureus.birthdayPackage');
      window.sessionStorage.removeItem('aureus.birthdayQty');
      window.sessionStorage.setItem(PRODUCTION_CAPITAL_CAMPAIGN.sessionKeys.packageId, packageId);
    } catch { /* ignore */ }
    if (onLaunchPackage) { onLaunchPackage(packageId); return; }
    window.location.href = '/purchase-shares';
  };

  return (
    <div className={`production-capital-packages birthday-package-buttons ${className}`.trim()}>
      <div className="pcap-header">
        <div className="pcap-title">{PRODUCTION_CAPITAL_CAMPAIGN.name}</div>
        <div className="pcap-subtitle">
          ${PRODUCTION_CAPITAL_CAMPAIGN.pricePerPaidShareUsd} per paid share — Packages 1–5 only
        </div>
      </div>
      <CampaignCountdown />
      <div className="pcap-package-grid" role="list">
        {packages.map((pkg) => {
          const b = computeProductionCapitalBreakdown(pkg.id)!;
          return (
            <button
              key={pkg.id}
              type="button"
              role="listitem"
              className="pcap-package-card"
              onClick={() => launch(pkg.id)}
            >
              <div className="pcap-card-name">{pkg.name}</div>
              <div className="pcap-card-price">
                Pay <strong>${b.priceUsd.toLocaleString()}</strong>
              </div>
              <ul className="pcap-card-stats">
                <li>Paid shares: <strong>{b.paidShares}</strong></li>
                <li>Bonus Shares: <strong className="pcap-bonus">+{b.investorBonus}</strong></li>
                <li>You receive: <strong>{b.investorTotal}</strong></li>
                <li>Sponsor bonus: <strong className="pcap-bonus">+{b.sponsorBonus}</strong></li>
              </ul>
              <span className="pcap-card-cta">Select {pkg.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Back-compat export name used by HomePage / dashboards */
export const BirthdayPackageButtons = ProductionCapitalPackageButtons;
export default ProductionCapitalPackageButtons;
